import type { CountryCode } from "@/config/country-config";
import type { ReportedDebtFlag } from "@/shared/flow-contracts";

export interface CountryRules {
	/** How the payout account is entered, validated and submitted. */
	payoutAccount: "swedish" | "finnish-iban";
	/** How a debt being redeemed is paid: bankgiro/plusgiro + OCR, or IBAN + reference. */
	redemptionPayment: "giro-ocr" | "iban-reference";
	/** FLOW's key for a refinance debt the credit register reported; false on one the customer added. */
	reportedDebtFlag: ReportedDebtFlag;
	/** Submitted instead of asking the customer; `null` asks. */
	fixedCitizenship: readonly string[] | null;
	autogiroConsent: boolean;
	/** The customer picks the payment due date. */
	dueDateChoice: boolean;
	secciDocument: boolean;
	saveAgreementNotice: boolean;
	/** A top-up settles the customer's existing Lea Bank loan; otherwise that loan is only shown. */
	settlesLeaBankLoan: boolean;
	/** Page requests on the App Service default hostname are sent to the public origin. */
	canonicalHostRedirect: boolean;
}

export const COUNTRY_RULES: Record<CountryCode, CountryRules> = {
	SE: {
		payoutAccount: "swedish",
		redemptionPayment: "giro-ocr",
		reportedDebtFlag: "isFromUc",
		fixedCitizenship: null,
		autogiroConsent: true,
		dueDateChoice: false,
		secciDocument: false,
		saveAgreementNotice: false,
		settlesLeaBankLoan: false,
		canonicalHostRedirect: true,
	},
	FI: {
		payoutAccount: "finnish-iban",
		redemptionPayment: "iban-reference",
		reportedDebtFlag: "isFromPcr",
		fixedCitizenship: ["FI"],
		autogiroConsent: false,
		dueDateChoice: true,
		secciDocument: true,
		saveAgreementNotice: true,
		settlesLeaBankLoan: true,
		canonicalHostRedirect: true,
	},
	// Mirrors SE until the Norwegian specification arrives, but the default
	// hostname is not redirected to the public origin yet.
	NO: {
		payoutAccount: "swedish",
		redemptionPayment: "giro-ocr",
		reportedDebtFlag: "isFromUc",
		fixedCitizenship: null,
		autogiroConsent: true,
		dueDateChoice: false,
		secciDocument: false,
		saveAgreementNotice: false,
		settlesLeaBankLoan: false,
		canonicalHostRedirect: false,
	},
};

export const getCountryRules = (country: CountryCode): CountryRules =>
	COUNTRY_RULES[country];
